// API locale - Remplacement de Baserow
// Le serveur Express expose les mêmes routes que Baserow
// Format des réponses identique : { count, next, previous, results }

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/database';

// Types pour les différentes tables (identiques à v1)
export interface Formalite {
  id: number;
  order: string;
  field_7251: string | null;           // Name (string)
  field_7260: Array<{ id: number; value: string; order: string }>;  // Customer (link_row)
  field_7261: Array<{ id: number; value: string; order: string }>;  // Company (link_row)
  field_7262: string | null;            // Description (string multiline)
  field_7263: Array<{ id: number; value: string; order: string }>;  // Formality Type (link_row)
  field_7264: string;                   // Creation Date (date, read-only)
  field_7265: string | null;            // Progression (string)
  field_7269: string | null;            // Deadline (date)
  field_7270: string | null;            // Forced Deadline (date)
  field_7271: string | null;            // Tax Deadline (date)
  field_7272: { id: number; value: string; color: string } | null; // Tax (single_select)
  field_7273: string;                   // Update Date (date, read-only)
  field_7274: string | null;            // Sender (string)
}

export interface Customer {
  id: number;
  order: string;
  field_7245: string;         // Name
  field_7254: string | null;  // Type (string)
  field_7255: string | null;  // Billing Address
  field_7256: string | null;  // Interlocutor Name
  field_7257: string | null;  // Interlocutor Phone
  field_7258: string | null;  // Interlocutor Mail
}

export interface Company {
  id: number;
  order: string;
  field_7248: string;         // Name
  field_7259: string | null;  // SIRET
}

export interface FormalityType {
  id: number;
  order: string;
  field_7266: string;         // Label
}

export interface CustomerType {
  id: number;
  order: string;
  field_7275: string;         // Label
}

export interface Client {
  id: number;
  order: string;
  field_7245: string;         // Nom du client
}

export interface Entreprise {
  id: number;
  entreprise: string;
  client: string;
  formalites: string;
}

export interface User {
  id: number;
  order: string;
  field_7278: string;         // user (nom de l'utilisateur)
  field_7279: string;         // email (email Google autorisé)
}

// IDs des tables (conservés pour compatibilité)
export const TABLES = {
  FORMALITIES: 757,
  CUSTOMERS: 755,
  COMPANIES: 756,
  FORMALITY_TYPES: 758,
  CUSTOMER_TYPES: 759,
  USERS: 760,
} as const;

class LocalAPI {
  // On utilise Record<string, string> pour autoriser l'ajout dynamique de clés (comme Authorization)
  private headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  private jwtToken: string | null = null;

  setJWTToken(token: string) {
    this.jwtToken = token;
  }

  clearJWTToken() {
    this.jwtToken = null;
  }

  // On précise que la fonction retourne un objet Record<string, string>
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = { ...this.headers };
    if (this.jwtToken) {
      headers['Authorization'] = `Bearer ${this.jwtToken}`;
    }
    return headers;
  }

  // Récupérer les données d'une table avec pagination et filtres
  async getRows<T>(tableId: number, options: {
    page?: number;
    size?: number;
    search?: string;
    order_by?: string;
    filters?: any;
  } = {}): Promise<{ count: number; next: string | null; previous: string | null; results: T[] }> {
    try {
      const params = new URLSearchParams();
      if (options.page) params.append('page', options.page.toString());
      if (options.size) params.append('size', options.size.toString());
      if (options.search) params.append('search', options.search);
      if (options.order_by) params.append('order_by', options.order_by);
      
      if (options.filters) {
        params.append('filters', JSON.stringify(options.filters));
      }

      const url = `${API_BASE_URL}/rows/table/${tableId}/?${params.toString()}`;
      const response = await fetch(url, {
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || errorData.detail || `Erreur HTTP ${response.status}: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error);
      throw error;
    }
  }

  // Créer une nouvelle ligne
  async createRow<T>(tableId: number, data: Partial<T>): Promise<T> {
    try {
      const response = await fetch(`${API_BASE_URL}/rows/table/${tableId}/`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Erreur API:', errorData);
        throw new Error(
          errorData.error || errorData.detail || `Erreur HTTP ${response.status}: ${response.statusText}`
        );
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la création de la ligne:', error);
      throw error;
    }
  }

  // Mettre à jour une ligne
  async updateRow<T>(tableId: number, rowId: number, data: Partial<T>): Promise<T> {
    try {
      const response = await fetch(`${API_BASE_URL}/rows/table/${tableId}/${rowId}/`, {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Erreur API:', errorData);
        throw new Error(
          errorData.error || errorData.detail || `Erreur HTTP ${response.status}: ${response.statusText}`
        );
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la ligne:', error);
      throw error;
    }
  }

  // Supprimer une ligne
  async deleteRow(tableId: number, rowId: number): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/rows/table/${tableId}/${rowId}/`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });
    } catch (error) {
      console.error('Erreur lors de la suppression de la ligne:', error);
      throw error;
    }
  }

  // Récupérer tous les clients pour les sélections
  async getAllClients(): Promise<Customer[]> {
    try {
      const allClients: Customer[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(
          `${API_BASE_URL}/rows/table/${TABLES.CUSTOMERS}/?page=${page}&size=200`,
          { headers: this.getHeaders() }
        );
        const data = await response.json();
        
        if (data.results && data.results.length > 0) {
          allClients.push(...data.results);
        }
        
        hasMore = !!data.next;
        page++;
      }

      return allClients;
    } catch (error) {
      console.error('Erreur lors de la récupération des clients:', error);
      return [];
    }
  }

  // Récupérer toutes les entreprises
  async getAllCompanies(): Promise<Company[]> {
    try {
      const allCompanies: Company[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(
          `${API_BASE_URL}/rows/table/${TABLES.COMPANIES}/?page=${page}&size=200`,
          { headers: this.getHeaders() }
        );
        const data = await response.json();
        
        if (data.results && data.results.length > 0) {
          allCompanies.push(...data.results);
        }
        
        hasMore = !!data.next;
        page++;
      }

      return allCompanies;
    } catch (error) {
      console.error('Erreur lors de la récupération des entreprises:', error);
      return [];
    }
  }

  // Récupérer tous les types de formalités
  async getAllFormalityTypes(): Promise<FormalityType[]> {
    try {
      const allTypes: FormalityType[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(
          `${API_BASE_URL}/rows/table/${TABLES.FORMALITY_TYPES}/?page=${page}&size=200`,
          { headers: this.getHeaders() }
        );
        const data = await response.json();
        
        if (data.results && data.results.length > 0) {
          allTypes.push(...data.results);
        }
        
        hasMore = !!data.next;
        page++;
      }

      return allTypes;
    } catch (error) {
      console.error('Erreur lors de la récupération des types de formalités:', error);
      return [];
    }
  }

  // Chercher les clients par nom
  async searchClients(searchTerm: string): Promise<Array<{ id: number; value: string }>> {
    try {
      const params = new URLSearchParams();
      params.append('search', searchTerm);
      params.append('size', '200');

      const response = await fetch(`${API_BASE_URL}/rows/table/${TABLES.CUSTOMERS}/?${params.toString()}`, {
        headers: this.getHeaders(),
      });
      const data = await response.json();
      
      return (data.results || []).map((client: any) => ({
        id: client.id,
        value: client.field_7245,
      }));
    } catch (error) {
      console.error('Erreur lors de la recherche de clients:', error);
      return [];
    }
  }

  // Filtrer les formalités
  async filterFormalites(filterCriteria: {
    field_7261?: string;
    field_7263?: string;
    field_7260?: string;
    page?: number;
  } = {}): Promise<{ count: number; next: string | null; previous: string | null; results: any[] }> {
    try {
      const filters: any[] = [];

      if (filterCriteria.field_7261?.trim()) {
        filters.push({
          field: 7261,
          type: 'link_row_contains',
          value: filterCriteria.field_7261.trim(),
        });
      }

      if (filterCriteria.field_7263?.trim()) {
        filters.push({
          field: 7263,
          type: 'link_row_contains',
          value: filterCriteria.field_7263.trim(),
        });
      }

      if (filterCriteria.field_7260?.trim()) {
        filters.push({
          field: 7260,
          type: 'link_row_contains',
          value: filterCriteria.field_7260.trim(),
        });
      }

      const params = new URLSearchParams();
      params.append('size', '100');
      params.append('page', (filterCriteria.page || 1).toString());

      if (filters.length > 0) {
        const filterJson = {
          filter_type: 'AND',
          filters: filters,
        };
        params.append('filters', JSON.stringify(filterJson));
      }

      const url = `${API_BASE_URL}/rows/table/${TABLES.FORMALITIES}/?${params.toString()}`;
      const response = await fetch(url, {
        headers: this.getHeaders(),
      });
      return await response.json();
    } catch (error) {
      console.error('Erreur lors du filtrage des formalités:', error);
      throw error;
    }
  }

  // Authentifier un utilisateur via Google: obtenir un JWT du backend
  async authenticateUserByEmail(email: string, name?: string): Promise<User | null> {
    try {
      const response = await fetch(
        `${API_BASE_URL.replace('/api/database', '')}/api/auth/login`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, name }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('Email non autorisé:', email, error);
        return null;
      }

      const data = await response.json();
      const { token, user } = data;

      // Stocker le JWT pour les requêtes futures
      this.setJWTToken(token);
      localStorage.setItem('auth_token', token);

      console.log('Utilisateur authentifié via Google:', user.field_7278, '-', email);
      return user;
    } catch (error) {
      console.error('Erreur lors de l\'authentification:', error);
      return null;
    }
  }

  // Restaurer le JWT depuis localStorage
  restoreJWTFromStorage() {
    const token = localStorage.getItem('auth_token');
    if (token) {
      this.setJWTToken(token);
    }
  }

  // Logout: supprimer le JWT
  logout() {
    this.clearJWTToken();
    localStorage.removeItem('auth_token');
  }

  // Chercher un utilisateur par son email
  async getUserByEmail(email: string): Promise<User | null> {
    try {
      const params = new URLSearchParams();
      params.append('search', email);
      params.append('size', '10');

      const response = await fetch(`${API_BASE_URL}/rows/table/${TABLES.USERS}/?${params.toString()}`, {
        headers: this.getHeaders(),
      });
      const data = await response.json();
      const users: User[] = data.results || [];

      return users.find(u => u.field_7279 === email) || null;
    } catch (error) {
      console.error('Erreur lors de la recherche d\'utilisateur:', error);
      return null;
    }
  }

  // Créer un nouvel utilisateur
  async createUser(username: string, email: string): Promise<User | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/rows/table/${TABLES.USERS}/`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          field_7278: username,
          field_7279: email,
        }),
      });
      
      if (!response.ok) {
        console.error('Erreur lors de la création d\'utilisateur');
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la création d\'utilisateur:', error);
      return null;
    }
  }
}

// Export avec le même nom que v1 pour compatibilité
export const baserowAPI = new LocalAPI();
